import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";

import {
  type AudioOutputOptions,
  JobStatus,
  ModuleType,
} from "Infrastructure/Api/Api";
import { useAppNavigate } from "Infrastructure/Routing/useAppNavigate";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { useModuleQuestionsQuery } from "Modules/Api/Queries/useModuleQuestionsQuery";
import { useSaveJobScriptMutation } from "Projects/Api/Mutations/useSaveJobScriptMutation";
import { useUpdateJobStatusMutation } from "Projects/Api/Mutations/useUpdateJobStatusMutation";
import { useJobDetailQuery } from "Projects/Api/Queries/useJobDetailQuery";
import { useProjectDetailQuery } from "Projects/Api/Queries/useProjectDetailQuery";
import {
  JobCreationWizard,
  type WizardMode,
} from "Projects/Components/JobCreationWizard";
import type { JobScriptFE } from "Projects/Components/ScriptEditor/ScriptEditorContext";
import {
  fromJobScriptFE,
  ScriptEditorProvider,
  toJobScriptFE,
} from "Projects/Components/ScriptEditor/ScriptEditorContext";
import type { ScriptValidationError } from "Projects/Utils/validateScript";
import {
  hasValidationErrors,
  validateScript,
} from "Projects/Utils/validateScript";
import { BackButton } from "Shared/Buttons/BackButton";
import { JobStatusChip } from "Shared/JobStatusChip";
import { AuthenticatedLayout } from "Shared/Layouts/AuthenticatedLayout";

export const ProjectJobDetailPage = (): React.ReactNode => {
  const { t } = useResource();
  const navigate = useAppNavigate();
  const { projectId, jobId } = useParams<{
    projectId: string;
    jobId: string;
  }>();

  const { data: job, isLoading } = useJobDetailQuery(projectId!, jobId!);
  const { data: project } = useProjectDetailQuery(projectId!);
  const { data: moduleQuestions } = useModuleQuestionsQuery(project?.moduleID);
  const saveScriptMutation = useSaveJobScriptMutation(projectId!, jobId!);
  const updateJobStatusMutation = useUpdateJobStatusMutation();

  const isSingleVoiceSpot = project?.moduleType === ModuleType.SingleVoiceSpot;

  // Track current script and audioOutputOptions via refs (updated by context callbacks)
  const scriptRef = useRef<JobScriptFE | null>(null);
  const audioOutputOptionsRef = useRef<AudioOutputOptions | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    ScriptValidationError[]
  >([]);
  const [mode, setMode] = useState<WizardMode>("choice");
  const modeInitializedRef = useRef(false);

  const isJobEditable = job?.status === JobStatus.InProgress;
  const isJobInactive = job?.status === JobStatus.Inactive;
  const isJobDone = job?.status === JobStatus.Done;

  useEffect(() => {
    if (job && !modeInitializedRef.current) {
      modeInitializedRef.current = true;
      const hasSegments = (job.script?.segments?.length ?? 0) > 0;
      const hasAnswers = (job.questionAnswers?.answers?.length ?? 0) > 0;
      const hasQuestions = (moduleQuestions?.length ?? 0) > 0;
      setMode(
        hasSegments
          ? "script"
          : hasAnswers
            ? "draft"
            : hasQuestions
              ? "choice"
              : "draft",
      );
      scriptRef.current = job.script
        ? toJobScriptFE(job.script)
        : {
            variables: [],
            segments: [],
            globalSpeedFactor: 1.0,
            globalVolumePercent: 100,
            defaultActorVoiceID: null,
            defaultLanguage: null,
          };
      audioOutputOptionsRef.current = job.script?.audioOutputOptions ?? null;
    }
  }, [job, moduleQuestions]);

  const handleScriptChange = useCallback((script: JobScriptFE) => {
    scriptRef.current = script;
    setValidationErrors(validateScript(script));
  }, []);

  const handleAudioOutputOptionsChange = useCallback(
    (opts: AudioOutputOptions) => {
      audioOutputOptionsRef.current = opts;
    },
    [],
  );

  const handleSave = async () => {
    const script = scriptRef.current;
    const audioOutputOptions = audioOutputOptionsRef.current;
    if (!script || !audioOutputOptions) return;

    const errors = validateScript(script);
    if (hasValidationErrors(errors)) {
      setValidationErrors(errors);
      toast.error(
        t(Resources.JobDetail.ScriptErrors, { count: errors.length }),
      );
      return;
    }

    try {
      await saveScriptMutation.mutateAsync(
        fromJobScriptFE(script, audioOutputOptions),
      );
      toast.success(t(Resources.JobDetail.SaveScriptSuccess));
    } catch {
      // auto-toast shown by mutation hook
    }
  };

  const handleStatusChange = async () => {
    if (!projectId || !jobId || !job) return;

    const isActive = job.status !== JobStatus.Inactive;

    try {
      await updateJobStatusMutation.mutateAsync({
        projectID: projectId,
        jobID: jobId,
        isActive: !isActive,
      });

      toast.success(
        isActive
          ? t(Resources.JobDetail.DeactivateSuccess)
          : t(Resources.JobDetail.ActivateSuccess),
      );
    } catch {
      toast.error(t(Resources.JobDetail.StatusChangeError));
    }
  };

  if (isLoading) {
    return (
      <AuthenticatedLayout title={t(Resources.Common.Projects)}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      </AuthenticatedLayout>
    );
  }

  if (!job) {
    return (
      <AuthenticatedLayout title={t(Resources.Common.Projects)}>
        <Typography>{t(Resources.JobDetail.JobNotFound)}</Typography>
      </AuthenticatedLayout>
    );
  }

  return (
    <ScriptEditorProvider
      initialScript={job.script}
      onScriptChange={handleScriptChange}
      onAudioOutputOptionsChange={handleAudioOutputOptionsChange}
      isSingleVoiceSpot={isSingleVoiceSpot}
      isEditable={isJobEditable}
    >
      <AuthenticatedLayout
        preTitle={
          <BackButton
            defaultText={t(Resources.Common.BackToProject)}
            defaultPath={AppRouting.ProjectDetail.path(projectId!)}
          />
        }
        subtitle={job.name}
        title={job.moduleName}
        titleEndAdornment={<JobStatusChip jobStatus={job.status} />}
        action={
          <Stack direction="row" spacing={Spacing.S}>
            {!isJobDone &&
              job.status !== JobStatus.WaitingForApproval &&
              job.status !== JobStatus.RejectedByActor && (
                <Button
                  variant="contained"
                  color={isJobInactive ? "success" : "error"}
                  onClick={handleStatusChange}
                  disabled={updateJobStatusMutation.isPending}
                  sx={{ textTransform: "none" }}
                >
                  {isJobInactive
                    ? t(Resources.JobDetail.Activate)
                    : t(Resources.JobDetail.Deactivate)}
                </Button>
              )}
            <Button
              variant="outlined"
              color="primary"
              onClick={() =>
                navigate(AppRouting.ProjectDetail.path(projectId!))
              }
              sx={{ textTransform: "none" }}
            >
              {t(Resources.Common.Cancel)}
            </Button>
            {isJobEditable && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saveScriptMutation.isPending || mode !== "script"}
                sx={{ textTransform: "none" }}
              >
                {saveScriptMutation.isPending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  t(Resources.Common.Save)
                )}
              </Button>
            )}
          </Stack>
        }
      >
        <Stack spacing={Spacing.M}>
          {!isJobEditable && (
            <Box
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1.25,
                borderRadius: Radius.M,
                border: `1px solid ${theme.palette.warning.main}`,
                backgroundColor: theme.colors.backgroundTile,
                color: theme.palette.warning.light,
              })}
            >
              <LockOutlinedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
              <Typography variant="body2" fontSize={13}>
                {t(Resources.JobDetail.JobReadOnlyBanner)}
              </Typography>
            </Box>
          )}
          <JobCreationWizard
            mode={mode}
            onModeChange={setMode}
            moduleQuestions={moduleQuestions}
            existingAnswers={job.questionAnswers?.answers}
            validationErrors={validationErrors}
            jobStatus={job.status}
            clips={job.clips}
          />
        </Stack>
      </AuthenticatedLayout>
    </ScriptEditorProvider>
  );
};
